import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuthenticated } from "../auth";
import { portalServer } from "../config";
import { usePortalApi } from "./portal-api";
import {
  useSyncData,
  useSyncModuleRegistration,
  type SyncModule,
} from "../sync";
import type { Sperrung, KartenObjektDatei } from "../types";
import { limiter } from "./demo";
import { daily } from "./provider";
import { precacheResources } from "./resource";
import { parseRoadClosureLines } from "./road-closure-lines";

const syncModuleId = "sperrungen";
const precacheSyncModuleId = "sperrungen:precache";

function useSperrungenSyncModule(): SyncModule<ReadonlyArray<Sperrung>> {
  const { portalKey, demo } = useAuthenticated();
  const { useNewApi, baseUrl } = usePortalApi();

  return useMemo(
    () => ({
      id: syncModuleId,
      i18nKey: `karte:tasks.${syncModuleId}`,
      defaultOptions: {
        schedule: { type: "periodic", interval: daily },
      },
      fetchData: () =>
        fetchSperrungen({ useNewApi, baseUrl, portalKey, demo }),
    }),
    [demo, portalKey, useNewApi, baseUrl],
  );
}

async function fetchSperrungen({
  useNewApi,
  baseUrl,
  portalKey,
  demo,
}: {
  useNewApi: boolean;
  baseUrl: string | undefined;
  portalKey: string;
  demo: boolean;
}): Promise<ReadonlyArray<Sperrung>> {
  const limited = limiter<Sperrung>(demo, 3);

  if (!useNewApi) {
    const response = await fetch(
      `${portalServer}/get-blocks.php?ORG_ACCESSKEY=${encodeURIComponent(
        portalKey,
      )}`,
    );

    if (!response.ok) {
      throw new Error(
        `Error fetching data: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();
    return limited(data.BLOCKS_LIST ?? []).map(parseSperrungLegacy);
  }

  // useNewApi is selected but no valid baseUrl is configured (e.g. an
  // unconfigured/invalid "custom" portal) — this is a configuration error,
  // not a reason to fall back to the legacy endpoint with a new-API
  // portalKey it can't use.
  if (!baseUrl) throw new Error("Kein Portal-Server konfiguriert.");

  return limited(await fetchSperrungenNew(portalKey, baseUrl));
}

async function fetchSperrungenNew(
  portalKey: string,
  baseUrl: string,
): Promise<ReadonlyArray<Sperrung>> {
  const response = await fetch(`${baseUrl}/api/device/sync/road-closures`, {
    headers: { Authorization: `Bearer ${portalKey}` },
  });

  if (!response.ok) {
    throw new Error(
      `Error fetching data: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();
  return (data.roadClosures ?? []).map((entry: any) =>
    parseSperrungNew(entry, portalKey, baseUrl),
  );
}

type RoadClosureType =
  | "VOLLSPERRUNG"
  | "TEILSPERRUNG"
  | "INFORMATION"
  | "BEDINGT_BEFAHRBAR"
  | "SONSTIGES";

function parseSperrungNew(
  entry: any,
  portalKey: string,
  baseUrl: string,
): Sperrung {
  const typeData = roadClosureTypeMap[entry.type as RoadClosureType] ??
    roadClosureTypeMap.VOLLSPERRUNG;

  const beschreibungParts: string[] = [];
  if (entry.reason) beschreibungParts.push(entry.reason);
  if (entry.remarks) beschreibungParts.push(entry.remarks);

  // Closure and detour can consist of several, unconnected lines
  const [segments = [], ...extraSegments] = parseRoadClosureLines(entry.closureMapData);
  const [detourSegments = [], ...extraDetourSegments] = parseRoadClosureLines(entry.detourMapData);

  const dateien: Array<KartenObjektDatei> = (entry.files ?? [])
    .toSorted((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0))
    .map((file: any) => ({
      id: file.id,
      name: file.originalName || file.filename,
      mimeType: file.mimeType,
      url: `${baseUrl}/api/device/sync/road-closures/files/${file.id}/download`,
      fetchOptions: { headers: { Authorization: `Bearer ${portalKey}` } },
    }));

  return {
    id: entry.id,
    typ: "sperrung",
    latitude: entry.latitude,
    longitude: entry.longitude,
    name: entry.name,
    ort: entry.address ?? "",
    adresse: entry.address ?? undefined,
    von: entry.dateFrom,
    bis: entry.dateTo,
    beschreibung: beschreibungParts.length > 0 ? beschreibungParts.join(" · ") : undefined,
    grund: entry.reason || undefined,
    hinweis: entry.remarks || undefined,
    richtung: entry.direction || undefined,
    gueltigkeitStatus: entry.validity || undefined,
    segments: segments.length > 1 ? segments : [],
    extraSegments: extraSegments.length > 0 ? extraSegments : undefined,
    detourSegments: detourSegments.length > 1 ? detourSegments : undefined,
    extraDetourSegments: extraDetourSegments.length > 0 ? extraDetourSegments : undefined,
    dateien: dateien.length > 0 ? dateien : undefined,
    ...typeData,
  };
}

const roadClosureTypeMap: Record<RoadClosureType, Partial<Sperrung>> = {
  VOLLSPERRUNG: {
    sperrungTyp: "Vollsperrung",
    icon: "sperrung",
    color: "#a22717",
    dashArray: "10 10",
  },
  TEILSPERRUNG: {
    sperrungTyp: "Teilsperrung",
    icon: "sperrung",
    color: "#a22717",
    dashArray: "10 10",
  },
  INFORMATION: {
    sperrungTyp: "Information",
    icon: "sperrung",
    color: "#a22717",
    dashArray: "10 10",
  },
  BEDINGT_BEFAHRBAR: {
    sperrungTyp: "Bedingt Befahrbar",
    icon: "bedingtbefahrbar",
    color: "#3d5afe",
    dashArray: "10 10",
  },
  SONSTIGES: {
    sperrungTyp: "Vollsperrung",
    icon: "sperrung",
    color: "#a22717",
    dashArray: "10 10",
  },
};

export function useSperrungenSync() {
  const module = useSperrungenSyncModule();
  useSyncModuleRegistration(module);
}

function useSperrungenPrecacheSyncModule(): SyncModule<void> {
  return useMemo(
    () => ({
      id: precacheSyncModuleId,
      i18nKey: `karte:tasks.${syncModuleId}.precache`,
      defaultOptions: {
        schedule: { type: "daily", hour: 3, minute: 0 },
        requiredNetworkStatus: ["wifi"],
      },
      dependsOn: [syncModuleId],
      fetchData: async ({ dataProvider, observer }) => {
        const sperrungen =
          dataProvider.getModuleData<ReadonlyArray<Sperrung>>(syncModuleId) ??
          [];
        const dateien = sperrungen.flatMap((s) => s.dateien ?? []);
        if (dateien.length === 0) return;

        await precacheResources(
          dateien.map((d) => d.url),
          { requestOptions: dateien[0].fetchOptions },
          observer,
        );
      },
    }),
    [],
  );
}

export function useSperrungenPrecacheSync() {
  const module = useSperrungenPrecacheSyncModule();
  useSyncModuleRegistration(module);
}

export function useSperrungen(): ReadonlyArray<Sperrung> {
  const { t } = useTranslation();
  const sperrungen = useSyncData<ReadonlyArray<Sperrung>>(syncModuleId);

  return useMemo(
    () =>
      (sperrungen ?? []).map((sperrung) => {
        if (!sperrung.richtung && !sperrung.gueltigkeitStatus) return sperrung;

        const parts = [sperrung.beschreibung];
        if (sperrung.richtung)
          parts.push(
            t(`karte:road-closures.direction.${sperrung.richtung}`, {
              defaultValue: sperrung.richtung,
            }),
          );
        if (sperrung.gueltigkeitStatus)
          parts.push(
            t(`karte:road-closures.validity.${sperrung.gueltigkeitStatus}`, {
              defaultValue: sperrung.gueltigkeitStatus,
            }),
          );

        return {
          ...sperrung,
          beschreibung: parts.filter(Boolean).join(" · "),
        };
      }),
    [sperrungen, t],
  );
}

function parseSperrungLegacy(entry: any): Sperrung {
  return {
    id: entry.BLOCK_ID,
    latitude: parseFloat(entry.BLOCK_GEO_LAT),
    longitude: parseFloat(entry.BLOCK_GEO_LNG),
    typ: "sperrung",
    sperrungTyp: "Vollsperrung",
    icon: "sperrung",
    color: "#a22717",
    dashArray: "10 10",
    name: entry.BLOCK_TEXT,
    ort: entry.BLOCK_PLACE,
    von: entry.BLOCK_TIME_FROM,
    bis: entry.BLOCK_TIME_TO,
    beschreibung: `${entry.BLOCK_PLACE}\nvon ${entry.BLOCK_TIME_FROM} bis ${entry.BLOCK_TIME_TO}`,
    segments:
      Array.isArray(entry.BLOCK_ARRAY) && entry.BLOCK_ARRAY.length > 1
        ? entry.BLOCK_ARRAY.map((seg: string[]) => seg.map(parseFloat))
        : [],
    ...parseSperrungTypLegacy(entry.BLOCK_TYPE),
  };
}

function parseSperrungTypLegacy(typ: string): Partial<Sperrung> {
  switch (typ) {
    case "1":
      return { sperrungTyp: "Teilsperrung", icon: "sperrung" };
    case "2":
      return { sperrungTyp: "Information", icon: "sperrung" };
    case "3":
      return {
        sperrungTyp: "Bedingt Befahrbar",
        icon: "bedingtbefahrbar",
        color: "#3d5afe",
      };
    case "4":
      return {
        sperrungTyp: "Behelfsweg",
        icon: "behelfsweg",
        color: "#4caf50",
      };
    case "5":
      return {
        sperrungTyp: "Befahrbarer Weg",
        icon: "befahrbarerweg",
        color: "#007F0E",
        dashArray: "",
        opacity: 0.5,
      };
    default:
      return {};
  }
}
